import api from "./api/axiosInstance";
import { API_BASE, API_URL } from "../config/env";

/**
 * Returns the full image URL or a fallback.
 * Supports both Vite (import.meta.env) and CRA/Webpack (process.env).
 *
 * @param {string} imagePath - path from DB (e.g. "/uploads/products/abc.jpg")
 * @param {string} fallback - fallback image path
 * @returns {string}
 */

/**
 * First usable image path on a product (or listing) record.
 */
export const resolveProductImagePath = (product) => {
  if (!product || typeof product !== "object") return null;

  const candidates = [
    ...(Array.isArray(product.images) ? product.images : []),
    product.image,
    product.thumbnail,
    product.sourceMetadata?.image,
    product.sourceMetadata?.imageUrl,
    product.sourceUrl,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      const chosen = candidate.trim();
      return chosen;
    }
  }

  return null;
};

const DEFAULT_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3E%3Crect fill='%23f1f5f9' width='300' height='200'/%3E%3Cpath fill='%23cbd5e1' d='M150 70c-16.5 0-30 13.5-30 30s13.5 30 30 30 30-13.5 30-30-13.5-30-30-30zm0 50c-11 0-20-9-20-20s9-20 20-20 20 9 20 20-9 20-20 20z'/%3E%3Cpath fill='%23cbd5e1' d='M180 60h-15l-5-10h-20l-5 10h-15c-5.5 0-10 4.5-10 10v60c0 5.5 4.5 10 10 10h60c5.5 0 10-4.5 10-10V70c0-5.5-4.5-10-10-10zm0 70H120V70h60v60z'/%3E%3C/svg%3E";

const getImageUrl = (imagePath, fallback = DEFAULT_FALLBACK) => {
  // Prefer absolute API origin; empty/relative means same-origin (prod nginx).
  let baseUrl =
    api?.defaults?.baseURL?.replace(/\/api\/?$/, "") ||
    API_BASE ||
    API_URL?.replace(/\/api\/?$/, "") ||
    "";

  // remove trailing slash for safety
  baseUrl = String(baseUrl).replace(/\/+$/, "");

  if (typeof imagePath !== "string" || !imagePath.trim()) {
    return fallback;
  }

  // If already absolute (external image), return as is
  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  // Otherwise, prefix with base URL
  return `${baseUrl}${imagePath.startsWith("/") ? imagePath : `/${imagePath}`}`;
};

/** Alias used across product components */
export const getProductImageUrl = (imagePath, fallback) =>
  getImageUrl(imagePath, fallback);

export default getImageUrl;
